import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router';

import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle
} from '@mui/material';

import useStores from 'hooks/use-stores';
import gtagEvent from 'main/components/utils/gtag-event';
import isOkResponse from 'utils/is-ok-response';

export default function useTrialModal() {
    const history = useHistory();
    const { userStore } = useStores();

    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({
        level: 'info',
        text: 'Are you sure want to start 30-days Trial Period?'
    });

    const {
        user: { isAnonymous },
        setTrialLicense,
        loadLicenseList
    } = userStore;

    const openModal = useCallback(() => setOpen(true), []);
    const closeModal = useCallback(() => setOpen(false), []);

    const onAnchorClick = useCallback(() => {
        if (isAnonymous) {
            history.push('/trial');
        } else {
            openModal();
        }
        gtagEvent('trial_click');
    }, [history, isAnonymous, openModal]);

    const handleStartTrialPeriod = useCallback(() => {
        setLoading(true);
        setTrialLicense()
            .then((response) => {
                if (isOkResponse(response)) {
                    loadLicenseList();
                    setMessage({
                        level: 'success',
                        text: 'Hooray! Now you are able to use Maximum features of Vulners.com for nearest 30 days!'
                    });
                } else {
                    setMessage({
                        level: 'success',
                        text: response?.data?.error || 'Something went wrong'
                    });
                }
            })
            .finally(() => {
                setLoading(false);
            });
    }, [loadLicenseList, setTrialLicense]);

    const modal = useMemo(
        () => (
            <Dialog open={open} onClose={closeModal}>
                <DialogTitle>{'Starting Trial License'}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{message.text}</DialogContentText>
                </DialogContent>
                {message.level === 'success' ? (
                    <DialogActions>
                        <Button onClick={closeModal} data-testid="close">
                            {'Close'}
                        </Button>
                        <Button href="/docs/api_reference/api/" color="primary" target="_blank" data-testid="readDocs">
                            {'Read Docs'}
                        </Button>
                        <Button href="/scan" color="primary" data-testid="startScan">
                            {'Start Scan'}
                        </Button>
                    </DialogActions>
                ) : (
                    <DialogActions>
                        <Button onClick={closeModal} color="primary" disabled={loading} data-testid="cancel">
                            {'Cancel'}
                        </Button>
                        <Button
                            onClick={handleStartTrialPeriod}
                            color="primary"
                            autoFocus
                            disabled={loading}
                            data-testid="startTrial"
                        >
                            {loading && (
                                <Box mr={1}>
                                    <CircularProgress size={24} />
                                </Box>
                            )}
                            {'Start trial'}
                        </Button>
                    </DialogActions>
                )}
            </Dialog>
        ),
        [closeModal, handleStartTrialPeriod, loading, open, message]
    );

    return { modal, onAnchorClick };
}
